"use client";

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore,
} from "react";
import { useAuth } from "@/contexts/auth-context";
import { useToast } from "@/contexts/toast-context";
import {
  applyDismissedBetOps,
  coerceDismissedBetsPayload,
  DISMISSED_BETS_REQUEST_TIMEOUT_MS,
  dismissedBetsMapsEqual,
  getDismissedBetCount,
  type DismissedBetMetadata,
  type DismissedBetOp,
  type DismissedBetsMap,
} from "@/lib/dismissal-fingerprints";
import {
  trackBetDismissed,
  trackBetRestored,
  type BetDismissalAnalyticsProps,
} from "@/utils/analytics";

export const DISMISSED_BETS_REFRESH_INTERVAL_MS = 15_000;

type DismissedBetTrackingContext = Partial<
  Omit<BetDismissalAnalyticsProps, "fingerprint" | "page">
> & {
  page?: BetDismissalAnalyticsProps["page"];
  [key: string]: unknown;
};

export interface DismissedBetMutationOptions {
  tracking?: DismissedBetTrackingContext;
  showUndoToast?: boolean;
  toastTitle?: string;
  toastDescription?: string;
  toastDuration?: number;
  dismissedAt?: number;
  meta?: DismissedBetMetadata;
}

export type DismissedBetEntry = {
  fingerprint: string;
  meta?: DismissedBetMetadata;
  tracking?: DismissedBetTrackingContext;
};

export type HideDismissedBetsOptions = Omit<DismissedBetMutationOptions, "meta" | "tracking"> & {
  onUndo?: () => void;
};

export interface RestoreDismissedBetOptions {
  tracking?: DismissedBetTrackingContext;
  showToast?: boolean;
  toastTitle?: string;
  toastDescription?: string;
}

interface DismissedBetsContextValue {
  dismissedBets: DismissedBetsMap;
  dismissedBetFingerprints: Set<string>;
  count: number;
  isManagerOpen: boolean;
  openManager: () => void;
  closeManager: () => void;
  hideBet: (fingerprint: string | null | undefined, options?: DismissedBetMutationOptions) => void;
  hideBets: (entries: readonly DismissedBetEntry[], options?: HideDismissedBetsOptions) => void;
  restoreBet: (fingerprint: string | null | undefined, options?: RestoreDismissedBetOptions) => void;
}

const DismissedBetsContext = createContext<DismissedBetsContextValue | undefined>(undefined);

async function requestDismissedBets(
  userId: string,
  ops: readonly DismissedBetOp[],
): Promise<DismissedBetsMap | null> {
  try {
    const signal = AbortSignal.timeout(DISMISSED_BETS_REQUEST_TIMEOUT_MS);
    const response = ops.length > 0
      ? await fetch("/api/dismissed-bets", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId, ops }),
        signal,
      })
      : await fetch(`/api/dismissed-bets?${new URLSearchParams({ userId })}`, { cache: "no-store", signal });
    if (!response.ok) return null;
    return coerceDismissedBetsPayload(await response.json())?.state.hidden ?? null;
  } catch {
    return null;
  }
}

function landed(op: DismissedBetOp, hidden: DismissedBetsMap): boolean {
  return (op.type === "hide") === (op.fingerprint in hidden);
}

function createDismissedBetsStore(
  initial: DismissedBetsMap | null,
  onWriteFailed: (ops: readonly DismissedBetOp[]) => void,
) {
  let userId: string | null = null;
  let account = 0;
  let server = initial;
  let pending: readonly DismissedBetOp[] = [];
  let readDue = false;
  let busy = false;
  const viewOf = () => applyDismissedBetOps(server ?? {}, pending);
  let view = viewOf();
  const listeners = new Set<() => void>();

  const publish = () => {
    const next = viewOf();
    if (dismissedBetsMapsEqual(view, next)) return;
    view = next;
    listeners.forEach((listener) => listener());
  };

  const run = async () => {
    if (busy) return;
    busy = true;
    try {
      while (userId && (readDue || pending.length > 0)) {
        const sentFor = account;
        const sent = pending;
        readDue = false;
        let hidden = await requestDismissedBets(userId, sent);
        if (sentFor !== account) continue;
        const unknown = hidden === null && sent.length > 0;
        if (unknown) {
          hidden = await requestDismissedBets(userId, []);
          if (sentFor !== account) continue;
        }
        pending = pending.slice(sent.length);
        if (hidden) server = hidden;
        if (unknown) {
          const later = new Set(pending.map((op) => op.fingerprint));
          const latest = [...new Map(sent.map((op) => [op.fingerprint, op])).values()];
          const lost = latest.filter((op) => !later.has(op.fingerprint) && !(hidden && landed(op, hidden)));
          if (lost.length > 0) onWriteFailed(lost);
        }
        publish();
      }
    } finally {
      busy = false;
    }
  };

  return {
    getSnapshot: () => view,
    subscribe: (listener: () => void) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    setAccount: (next: string | null): boolean => {
      if (next === userId) return false;
      const reset = userId !== null;
      userId = next;
      if (reset) {
        account += 1;
        server = null;
        pending = [];
        publish();
      }
      if (next && server === null) readDue = true;
      void run();
      return reset;
    },
    apply: (ops: readonly DismissedBetOp[]) => {
      if (ops.length === 0) return;
      pending = [...pending, ...ops];
      publish();
      queueMicrotask(() => void run());
    },
    refresh: () => {
      publish();
      readDue = true;
      void run();
    },
  };
}

function maybeTrackDismissed(fingerprint: string, tracking?: DismissedBetTrackingContext): void {
  if (!tracking?.page) return;
  trackBetDismissed({ ...tracking, fingerprint, page: tracking.page });
}

function maybeTrackRestored(fingerprint: string, tracking?: DismissedBetTrackingContext): void {
  if (!tracking?.page) return;
  trackBetRestored({ ...tracking, fingerprint, page: tracking.page });
}

export function DismissedBetsProvider({
  children,
  initialDismissedBets = null,
}: {
  children: React.ReactNode;
  initialDismissedBets?: DismissedBetsMap | null;
}) {
  const { user } = useAuth();
  const { showToast } = useToast();
  const userId = user?.id ?? null;
  const [store] = useState(() => createDismissedBetsStore(initialDismissedBets, (ops) => {
    if (ops.some((op) => op.type === "hide")) showToast("Could not hide bet. Try again.", "error");
    if (ops.some((op) => op.type === "restore")) showToast("Could not restore bet. Try again.", "error");
  }));
  const dismissedBets = useSyncExternalStore(store.subscribe, store.getSnapshot, store.getSnapshot);
  const [isManagerOpen, setIsManagerOpen] = useState(false);

  useEffect(() => {
    if (store.setAccount(userId)) setIsManagerOpen(false);
  }, [store, userId]);

  useEffect(() => {
    const refreshIfVisible = () => {
      if (document.visibilityState === "visible") store.refresh();
    };
    document.addEventListener("visibilitychange", refreshIfVisible);
    window.addEventListener("focus", refreshIfVisible);
    window.addEventListener("online", refreshIfVisible);
    const intervalId = setInterval(refreshIfVisible, DISMISSED_BETS_REFRESH_INTERVAL_MS);
    return () => {
      document.removeEventListener("visibilitychange", refreshIfVisible);
      window.removeEventListener("focus", refreshIfVisible);
      window.removeEventListener("online", refreshIfVisible);
      clearInterval(intervalId);
    };
  }, [store]);

  const restoreBet = useCallback((
    fingerprint: string | null | undefined,
    options: RestoreDismissedBetOptions = {},
  ) => {
    if (!fingerprint || !(fingerprint in store.getSnapshot())) return;
    store.apply([{ type: "restore", fingerprint }]);
    maybeTrackRestored(fingerprint, options.tracking);
    if (options.showToast) {
      showToast(options.toastDescription ?? "Bet restored.", "success", options.toastTitle);
    }
  }, [showToast, store]);

  const hideBets = useCallback((
    entries: readonly DismissedBetEntry[],
    options: HideDismissedBetsOptions = {},
  ) => {
    if (entries.length === 0) return;

    const dismissedAt = options.dismissedAt ?? Date.now();
    const current = store.getSnapshot();
    const fresh = entries.filter(({ fingerprint }) => !(fingerprint in current));
    store.apply(fresh.map(({ fingerprint, meta }) => ({
      type: "hide" as const,
      fingerprint,
      record: meta ? { dismissedAt, meta } : { dismissedAt },
    })));
    for (const { fingerprint, tracking } of fresh) {
      maybeTrackDismissed(fingerprint, tracking);
    }

    if (options.showUndoToast !== false) {
      showToast(
        options.toastDescription ?? "Bet hidden.",
        "info",
        options.toastTitle,
        options.toastDuration ?? 6_000,
        [
          {
            label: "Undo",
            ariaLabel: entries.length > 1 ? "Undo hidden bets" : "Undo hidden bet",
            onClick: () => {
              for (const { fingerprint, tracking } of fresh) {
                restoreBet(fingerprint, { tracking, showToast: false });
              }
              options.onUndo?.();
            },
          },
          {
            label: "Manage Hidden",
            ariaLabel: "Manage hidden bets",
            onClick: () => setIsManagerOpen(true),
          },
        ],
      );
    }
  }, [restoreBet, showToast, store]);

  const hideBet = useCallback((
    fingerprint: string | null | undefined,
    { meta, tracking, ...options }: DismissedBetMutationOptions = {},
  ) => {
    if (!fingerprint) return;
    hideBets([{ fingerprint, meta, tracking }], options);
  }, [hideBets]);

  const openManager = useCallback(() => setIsManagerOpen(true), []);
  const closeManager = useCallback(() => setIsManagerOpen(false), []);

  const dismissedBetFingerprints = useMemo(() => new Set(Object.keys(dismissedBets)), [dismissedBets]);

  const value = useMemo<DismissedBetsContextValue>(() => ({
    dismissedBets,
    dismissedBetFingerprints,
    count: getDismissedBetCount(dismissedBets),
    isManagerOpen,
    openManager,
    closeManager,
    hideBet,
    hideBets,
    restoreBet,
  }), [closeManager, dismissedBetFingerprints, dismissedBets, hideBet, hideBets, isManagerOpen, openManager, restoreBet]);

  return (
    <DismissedBetsContext.Provider value={value}>
      {children}
    </DismissedBetsContext.Provider>
  );
}

export function useDismissedBets(): DismissedBetsContextValue {
  const context = useContext(DismissedBetsContext);
  if (!context) {
    throw new Error("useDismissedBets must be used within a DismissedBetsProvider");
  }
  return context;
}

export function useOptionalDismissedBets(): DismissedBetsContextValue | undefined {
  return useContext(DismissedBetsContext);
}
